import { ExpandMore } from '@tunasong/icons'
import type { Melody } from '@tunasong/schemas'
import { Button, Menu, MenuContent, MenuItem, MenuTrigger } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useId, useState } from 'react'
import { MelodyTrainerDialog } from '../melody-trainer/melody-trainer-dialog.js'
import styles from './melody-practice-menu.module.css'
import { PhrasePracticeDialog } from './phrase-practice-dialog.js'
import type { PickerItem } from './picker-items.js'

/**
 * The melody's Practice menu (Pitch Trainer Phase 2, plan M6a step 6.0; Joni §1). It replaces the single
 * Practice button beside Capture, in the melody block and the melody editor. Two choices, each saying how it
 * differs before choosing: phrase by phrase at the singer's own pace, or the whole melody in time.
 */

export interface MelodyPracticeMenuProps {
  melody: Melody
  /** What the picker's rows are named after: the enclosing section's header name, else the melody's */
  sectionName: string
  /** Pitfall 6: "Edit melody" from the picker. Absent where the melody is already being edited */
  onEdit?: () => void
}

interface ChoiceProps {
  label: string
  description: string
  onSelect: () => void
}

const Choice: FC<ChoiceProps> = ({ label, description, onSelect }) => {
  const labelId = useId()
  const descriptionId = useId()
  return (
    <MenuItem className={styles.item} aria-labelledby={labelId} aria-describedby={descriptionId} onClick={onSelect}>
      <span id={labelId}>{label}</span>
      <span id={descriptionId} className={styles.description}>
        {description}
      </span>
    </MenuItem>
  )
}

export const MelodyPracticeMenu: FC<MelodyPracticeMenuProps> = props => {
  const { melody, sectionName, onEdit } = props
  const [open, setOpen] = useState<'phrase' | 'in-time' | null>(null)
  const close = () => setOpen(null)

  const handleEdit = (_item: PickerItem) => {
    close()
    onEdit?.()
  }

  return (
    <>
      <Menu>
        <MenuTrigger render={<Button endIcon={<ExpandMore fontSize="small" />}>Practice</Button>} />
        <MenuContent>
          <Choice
            label="Sing a phrase"
            description="At your own pace, one phrase at a time"
            onSelect={() => setOpen('phrase')}
          />
          <Choice
            label="Practice it in time"
            description="The whole melody, with a click"
            onSelect={() => setOpen('in-time')}
          />
        </MenuContent>
      </Menu>
      {open === 'phrase' ? (
        <PhrasePracticeDialog
          open={true}
          onClose={close}
          sources={[
            {
              kind: 'melody',
              id: melody.id ?? 'melody',
              section: sectionName,
              isChorus: false,
              melody,
              ...(melody.key ? { key: melody.key } : {}),
            },
          ]}
          onEdit={handleEdit}
        />
      ) : null}
      <MelodyTrainerDialog melody={melody} open={open === 'in-time'} onClose={close} />
    </>
  )
}
